import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  Grid,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Stack,
  Divider,
  Paper,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  TrendingUp as AccountingIcon,
  People as HRIcon,
  Apartment as TenantsIcon,
  BarChart as BudgetIcon,
  Business as GroupIcon,
  SmartToy as AIIcon,
  Chat as CommsIcon,
  Settings as SettingsIcon,
  Security as AuditIcon,
  Forum as MessagingIcon,
  Notifications as NotificationsIcon,
  GavelRounded as ApprovalsIcon,
  Receipt as BillingIcon,
  Shield as SecurityIcon,
  AccountBalance as PayrollIcon,
  PersonSearch as RecruitmentIcon,
  HowToReg as OnboardingIcon,
  EmojiEvents as PerformanceIcon,
  CreditCard as CreditCardIcon,
  CheckCircleOutline as EnableIcon,
  RemoveCircleOutline as DisableIcon,
  Lock as LockIcon,
  Campaign as CampaignIcon,
  SupportAgent as SupportAgentIcon,
  ContactMail as CrmIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { DynamicIcon } from '@/components/common/DynamicIcon';
import { moduleApi, ModuleConfig, TemplatePreview } from '@/api/endpoints/settings';
import { billingApi } from '@/api/endpoints/billing';
import { useAuthStore } from '@/stores/authStore';
import { useNotificationStore } from '@/stores/notificationStore';
import PageHelpIcon from '@/components/PageHelpIcon';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

// Which plan first includes each module key.
// Mirrors seed_plans.py PLAN_ORDER ladder exactly.
const MODULE_PLAN_INFO: Record<string, { planName: string; slug: string; priceHint: string }> = {
  onboarding:    { planName: 'Starter',      slug: 'starter',      priceHint: '₦20,000/mo' },
  comms:         { planName: 'Starter',      slug: 'starter',      priceHint: '₦20,000/mo' },
  budget:        { planName: 'Growth',       slug: 'growth',       priceHint: '₦65,000/mo' },
  recruitment:   { planName: 'Growth',       slug: 'growth',       priceHint: '₦65,000/mo' },
  performance:   { planName: 'Growth',       slug: 'growth',       priceHint: '₦65,000/mo' },
  tenants:       { planName: 'Professional', slug: 'professional', priceHint: '₦150,000/mo' },
  ai:            { planName: 'Professional', slug: 'professional', priceHint: '₦150,000/mo' },
  group:         { planName: 'Enterprise',   slug: 'enterprise',   priceHint: 'Contact sales' },
  tax:           { planName: 'Starter',      slug: 'starter',      priceHint: '₦20,000/mo' },
  crm:           { planName: 'Professional', slug: 'professional', priceHint: '₦150,000/mo' },
  marketplace:   { planName: 'Professional', slug: 'professional', priceHint: '₦150,000/mo' },
};

const ICON_MAP: Record<string, React.ReactNode> = {
  Dashboard: <DashboardIcon />,
  TrendingUp: <AccountingIcon />,
  People: <HRIcon />,
  Apartment: <TenantsIcon />,
  BarChart: <BudgetIcon />,
  Business: <GroupIcon />,
  SmartToy: <AIIcon />,
  Chat: <CommsIcon />,
  Settings: <SettingsIcon />,
  Security: <AuditIcon />,
  Forum: <MessagingIcon />,
  Notifications: <NotificationsIcon />,
  GavelRounded: <ApprovalsIcon />,
  Receipt: <BillingIcon />,
  Shield: <SecurityIcon />,
  AccountBalance: <PayrollIcon />,
  PersonSearch: <RecruitmentIcon />,
  HowToReg: <OnboardingIcon />,
  EmojiEvents: <PerformanceIcon />,
  CreditCard: <CreditCardIcon />,
};

const COMING_SOON_MODULES = [
  {
    key: 'crm',
    name: 'CRM',
    icon: <CrmIcon />,
    description: 'Manage your customer pipeline, contacts, and accounts — a full B2B sales layer for your business.',
  },
  {
    key: 'pulse',
    name: 'Pulse — Sidekick',
    icon: <SupportAgentIcon />,
    description: 'AI-powered customer support inbox. Auto-classify, route, and resolve inbound requests without leaving the platform.',
  },
  {
    key: 'marketing',
    name: 'Marketing',
    icon: <CampaignIcon />,
    description: 'Email campaigns, drip sequences, and suppression management to reach and nurture your customers at scale.',
  },
];

function moduleNameMap(modules?: ModuleConfig[]): Record<string, string> {
  const out: Record<string, string> = {};
  modules?.forEach((m) => {
    out[m.key] = m.name;
  });
  return out;
}

function namesFor(keys: string[], names: Record<string, string>): string {
  if (!keys.length) return '';
  return keys.map((k) => names[k] || k).join(', ');
}

export default function ModuleConfigPage({ embedded }: { embedded?: boolean }) {
  const { activeCompanyId, companies, updateCompanyModules } = useAuthStore();
  const activeCompany = companies.find((c) => c.company_id === activeCompanyId);
  const queryClient = useQueryClient();
  const { showSuccess, showError, showInfo } = useNotificationStore();

  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, boolean>>({});
  const [planBlockedAfterApply, setPlanBlockedAfterApply] = useState<string[]>([]);
  const [buyingAddon, setBuyingAddon] = useState<string | null>(null);
  const navigate = useNavigate();

  // Backend-derived flag — covers Company / Group / legacy System Administrator.
  // Survives future role renames and tenants on DBs where the Sprint-1 rename
  // migration (prodfed1a2b3c) has not yet been applied.
  const isAdmin = activeCompany?.is_tenant_admin === true;

  const { data: modules, isLoading } = useQuery({
    queryKey: ['module-config', activeCompanyId],
    queryFn: () => moduleApi.getModuleConfig().then((res) => res.data),
    enabled: !!activeCompanyId,
  });

  const { data: templates } = useQuery({
    queryKey: ['module-templates'],
    queryFn: () => moduleApi.getTemplates().then((res) => res.data),
    enabled: isAdmin,
  });

  const { data: appliedTemplate } = useQuery({
    queryKey: ['module-applied-template', activeCompanyId],
    queryFn: () => moduleApi.getAppliedTemplate().then((res) => res.data),
    enabled: !!activeCompanyId,
  });

  // Live subscription — its plan's enabled_modules tells us which modules are
  // included in the current plan vs. only available as paid add-ons.
  const { data: subscription } = useQuery({
    queryKey: ['module-config-subscription', activeCompanyId],
    queryFn: () => billingApi.getSubscription().then((res) => res.data),
    enabled: !!activeCompanyId,
    retry: false,
  });

  // The full plan list — needed because SubscriptionWithUsage exposes the plan
  // slug/name but not its enabled_modules; we look those up here by plan_id.
  const { data: plans } = useQuery({
    queryKey: ['module-config-plans'],
    queryFn: () => billingApi.listPlans().then((res) => res.data),
    enabled: !!activeCompanyId,
    retry: false,
  });

  // À-la-carte add-ons offered for this company. `already_active` marks the
  // ones the company currently holds (active add-on) vs. purchasable (available).
  const { data: availableAddons } = useQuery({
    queryKey: ['module-config-available-addons', activeCompanyId],
    queryFn: () => billingApi.getAvailableAddons().then((res) => res.data.addons),
    enabled: !!activeCompanyId,
    retry: false,
  });

  // A4: read-only diff of what the selected template would change. Driven by
  // the dialog open state — only fetched while the user is staring at it.
  const { data: preview, isFetching: previewFetching } = useQuery<TemplatePreview>({
    queryKey: ['module-template-preview', selectedTemplate],
    queryFn: () =>
      moduleApi.previewTemplate(selectedTemplate).then((res) => res.data),
    enabled: confirmDialog && !!selectedTemplate,
  });

  const names = useMemo(() => moduleNameMap(modules), [modules]);

  // ── Four-state module categorization (live API) ──
  // Core           → mod.is_core
  // Plan-included  → key in the current plan's enabled_modules
  // Add-on active  → key offered as an add-on AND already held (already_active)
  // Available      → key offered as an add-on but not yet held
  // Anything that fits none of the above (a non-core toggleable module that the
  // plan/add-on data doesn't classify) falls into `other`, rendered as a plain
  // toggle card so no module silently disappears.
  type ModuleState = 'core' | 'plan' | 'addon_active' | 'available' | 'other';

  const planModuleKeys = useMemo(() => {
    if (!subscription || !plans) return new Set<string>();
    const plan = plans.find((p) => p.id === subscription.plan_id)
      ?? plans.find((p) => p.slug === subscription.plan_slug);
    return new Set<string>(plan?.enabled_modules ?? []);
  }, [subscription, plans]);

  const addonByKey = useMemo(() => {
    const m = new Map<string, NonNullable<typeof availableAddons>[number]>();
    (availableAddons ?? []).forEach((a) => m.set(a.module_key, a));
    return m;
  }, [availableAddons]);

  const classify = (mod: ModuleConfig): ModuleState => {
    if (mod.is_core) return 'core';
    if (planModuleKeys.has(mod.key)) return 'plan';
    const addon = addonByKey.get(mod.key);
    if (addon) return addon.already_active ? 'addon_active' : 'available';
    return 'other';
  };

  const categorized = useMemo(() => {
    const groups: Record<ModuleState, ModuleConfig[]> = {
      core: [], plan: [], addon_active: [], available: [], other: [],
    };
    (modules ?? []).forEach((mod) => { groups[classify(mod)].push(mod); });
    return groups;
    // classify depends on planModuleKeys + addonByKey, both memoized below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules, planModuleKeys, addonByKey]);

  // Modules that require a plan upgrade (not in current plan, not an add-on).
  // These get the "Unlock with a plan upgrade" treatment rather than a buy button.
  const planLockedModules = useMemo(
    () =>
      (modules ?? []).filter(
        (mod) => !mod.is_core && !planModuleKeys.has(mod.key) && !addonByKey.has(mod.key),
      ),
    [modules, planModuleKeys, addonByKey],
  );

  const applyTemplateMutation = useMutation({
    mutationFn: (templateId: string) => moduleApi.applyTemplate(templateId),
    onSuccess: (res) => {
      const data = res.data;
      const enabledKeys = data.config.filter((m) => m.is_enabled).map((m) => m.key);
      if (activeCompanyId) {
        updateCompanyModules(activeCompanyId, enabledKeys);
      }
      queryClient.invalidateQueries({ queryKey: ['module-config'] });
      queryClient.invalidateQueries({ queryKey: ['module-applied-template'] });
      setPendingChanges({});
      setConfirmDialog(false);
      setSelectedTemplate('');
      const enabled = data.diff.enabled;
      const disabled = data.diff.disabled;
      if (enabled.length || disabled.length) {
        const parts: string[] = [];
        if (enabled.length) parts.push(`Enabled: ${namesFor(enabled, names)}`);
        if (disabled.length) parts.push(`Disabled: ${namesFor(disabled, names)}`);
        showSuccess(`Template applied. ${parts.join(' · ')}`);
      } else {
        showInfo('Template applied — no changes (already matched).');
      }
      setPlanBlockedAfterApply(data.plan_blocked || []);
    },
    onError: (err: any) => {
      showError(err.response?.data?.detail || 'Failed to apply template');
      setConfirmDialog(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (moduleUpdates: { key: string; enabled: boolean }[]) =>
      moduleApi.updateModuleConfig(moduleUpdates),
    onSuccess: (res) => {
      const data = res.data;
      const enabledKeys = data.config.filter((m) => m.is_enabled).map((m) => m.key);
      if (activeCompanyId) {
        updateCompanyModules(activeCompanyId, enabledKeys);
      }
      queryClient.invalidateQueries({ queryKey: ['module-config'] });
      setPendingChanges({});
      const enabled = data.diff.enabled;
      const disabled = data.diff.disabled;
      if (enabled.length || disabled.length) {
        const parts: string[] = [];
        if (enabled.length) parts.push(`Enabled: ${namesFor(enabled, names)}`);
        if (disabled.length) parts.push(`Disabled: ${namesFor(disabled, names)}`);
        showSuccess(parts.join(' · '));
      } else {
        showInfo('No module changes to save.');
      }
    },
    onError: (err: any) => {
      showError(err.response?.data?.detail || 'Failed to update modules');
    },
  });

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  // In-app nav blocker needs react-router's data router; this app uses
  // <BrowserRouter>, so we rely on the unsaved-changes banner + the
  // beforeunload handler below. A true nav blocker requires migrating
  // App.tsx to createBrowserRouter/RouterProvider.
  // beforeunload: catch full page reload / browser close.
  useEffect(() => {
    if (!hasPendingChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasPendingChanges]);

  // A5: soft non-admin view — read-only chip list instead of a hard error Alert.
  if (!isAdmin) {
    const enabled = modules?.filter((m) => m.is_enabled) ?? [];
    return (
      <Box sx={{ p: embedded ? 0 : 3, maxWidth: 800 }}>
        <Box sx={{ mb: 1 }}>
          <PageHelpIcon pageKey="settings.modules" />
        </Box>
        <Alert severity="info" icon={<LockIcon fontSize="small" />} sx={{ mb: 2 }}>
          Modules are managed by your administrator. Ask them to change which features are enabled
          for {activeCompany?.company_name || 'this company'}.
        </Alert>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: TYPE.semibold }}>
              Currently enabled
            </Typography>
            {isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {enabled.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No modules enabled.
                  </Typography>
                )}
                {enabled.map((m) => (
                  <Chip key={m.key} label={m.name} size="small" variant="outlined" />
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  const handleToggle = (key: string, currentEnabled: boolean) => {
    setPendingChanges((prev) => {
      const next = { ...prev, [key]: !currentEnabled };
      // If the new pending value matches the persisted state, drop the entry —
      // toggling a switch back to its original position should clear the
      // "unsaved changes" banner instead of leaving a no-op pending change.
      const persisted = modules?.find((m) => m.key === key)?.is_enabled ?? true;
      if (next[key] === persisted) delete next[key];
      return next;
    });
  };

  const getEffectiveEnabled = (mod: ModuleConfig) => {
    if (mod.key in pendingChanges) return pendingChanges[mod.key];
    return mod.is_enabled;
  };

  const handleSave = () => {
    if (!modules) return;
    const updates = modules
      .filter((m) => !m.is_core)
      .map((m) => ({
        key: m.key,
        enabled: m.key in pendingChanges ? pendingChanges[m.key] : m.is_enabled,
      }));
    updateMutation.mutate(updates);
  };

  const handleDiscard = () => setPendingChanges({});

  const handleApplyTemplate = () => {
    if (selectedTemplate) {
      setConfirmDialog(true);
      setPlanBlockedAfterApply([]);
    }
  };

  // Buy an available add-on → gateway checkout (recurring). On success the
  // gateway webhook flips the company_product to active and the module moves
  // into the "Add-on active" group on the next load.
  const handleBuyAddon = async (moduleKey: string, interval: 'monthly' | 'annual') => {
    setBuyingAddon(moduleKey);
    try {
      const res = await billingApi.checkoutAddon(moduleKey, interval);
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      showError('Checkout created but no redirect URL was returned.');
      setBuyingAddon(null);
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Could not start add-on checkout');
      setBuyingAddon(null);
    }
  };

  const selectedTemplateName = templates?.find((t) => t.id === selectedTemplate)?.name;

  if (isLoading) {
    return (
      <Box sx={{ p: embedded ? 0 : 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, position: 'relative' }}>
      <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <PageHelpIcon pageKey="settings.modules" />
        {appliedTemplate?.key && (
          <Chip
            label={`Based on: ${appliedTemplate.name || appliedTemplate.key}`}
            size="small"
            variant="outlined"
            color="primary"
          />
        )}
      </Box>

      {/* A3: sticky dirty-state banner. Only renders when there are unsaved toggles. */}
      {hasPendingChanges && (
        <Alert
          severity="warning"
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            mb: 2,
            alignItems: 'center',
            '& .MuiAlert-message': { flex: 1 },
          }}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                color="inherit"
                onClick={handleDiscard}
                disabled={updateMutation.isPending}
              >
                Discard
              </Button>
              <Button
                size="small"
                variant="contained"
                color="warning"
                onClick={handleSave}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </Stack>
          }
        >
          You have {Object.keys(pendingChanges).length} unsaved module change
          {Object.keys(pendingChanges).length === 1 ? '' : 's'}.
        </Alert>
      )}

      {/* B4: plan-blocked alert after applying a template. */}
      {planBlockedAfterApply.length > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => setPlanBlockedAfterApply([])}
        >
          {planBlockedAfterApply.length} module
          {planBlockedAfterApply.length === 1 ? '' : 's'} from this template require a higher plan
          and weren&rsquo;t enabled: <strong>{namesFor(planBlockedAfterApply, names)}</strong>.
          Upgrade your plan to enable them.
        </Alert>
      )}

      {/* Template Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>Industry Template</InputLabel>
            <Select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              label="Industry Template"
            >
              {templates?.map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: TYPE.medium }}>
                      {t.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            onClick={handleApplyTemplate}
            disabled={!selectedTemplate || applyTemplateMutation.isPending}
          >
            Preview &amp; apply
          </Button>
          {selectedTemplate && templates && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {templates
                .find((t) => t.id === selectedTemplate)
                ?.enabled_modules.map((m) => (
                  <Chip key={m} label={names[m] || m} size="small" variant="outlined" />
                ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Module sections — four states derived from live billing data:
          Core · Plan-included · Add-on active · Available to buy. */}
      <ModuleStateSection
        title="Core"
        caption="Always on — part of every plan."
        icon={<EnableIcon fontSize="small" color="primary" />}
        chip={<Chip label="Core" size="small" color="primary" variant="outlined" />}
        modules={categorized.core}
        renderControl={() => (
          <Tooltip title="Core module — always enabled">
            <Chip label="Core" size="small" color="primary" variant="outlined" />
          </Tooltip>
        )}
        getEffectiveEnabled={getEffectiveEnabled}
        iconMap={ICON_MAP}
      />

      <ModuleStateSection
        title="Included in your plan"
        caption="Bundled with your current subscription — toggle on or off any time."
        icon={<EnableIcon fontSize="small" color="success" />}
        chip={<Chip label={`${categorized.plan.length}`} size="small" variant="outlined" />}
        modules={categorized.plan}
        renderControl={(mod) => (
          <Switch
            checked={getEffectiveEnabled(mod)}
            onChange={() => handleToggle(mod.key, getEffectiveEnabled(mod))}
            size="small"
            disabled={updateMutation.isPending || applyTemplateMutation.isPending}
            aria-label={`Toggle ${mod.name} module`}
          />
        )}
        getEffectiveEnabled={getEffectiveEnabled}
        iconMap={ICON_MAP}
      />

      <ModuleStateSection
        title="Active add-ons"
        caption="Paid extras you currently subscribe to."
        icon={<CreditCardIcon fontSize="small" color="success" />}
        chip={<Chip label="Add-on" size="small" color="success" variant="outlined" />}
        modules={categorized.addon_active}
        renderControl={(mod) => (
          <Switch
            checked={getEffectiveEnabled(mod)}
            onChange={() => handleToggle(mod.key, getEffectiveEnabled(mod))}
            size="small"
            disabled={updateMutation.isPending || applyTemplateMutation.isPending}
            aria-label={`Toggle ${mod.name} module`}
          />
        )}
        getEffectiveEnabled={getEffectiveEnabled}
        iconMap={ICON_MAP}
      />

      {/* Plan-upgrade locked modules — modules not included in current plan */}
      {planLockedModules.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LockIcon fontSize="small" sx={{ color: 'warning.main' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: TYPE.semibold }}>
              Unlock with a plan upgrade
            </Typography>
            <Chip
              label={`${planLockedModules.length} module${planLockedModules.length > 1 ? 's' : ''}`}
              size="small"
              color="warning"
              variant="outlined"
            />
          </Box>
          <Grid container spacing={2}>
            {planLockedModules.map((mod) => {
              const info = MODULE_PLAN_INFO[mod.key];
              return (
                <Grid item xs={12} sm={6} md={4} key={mod.key}>
                  <Paper
                    variant="outlined"
                    sx={{
                      height: '100%',
                      borderColor: 'warning.light',
                      borderStyle: 'dashed',
                      borderRadius: 2,
                      bgcolor: 'action.hover',
                      opacity: 0.85,
                      p: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ color: 'text.disabled' }}>
                          {ICON_MAP[mod.icon ?? ''] ?? (
                            <DynamicIcon name={mod.icon} fallback={LockIcon} />
                          )}
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: TYPE.semibold, color: 'text.secondary' }}>
                          {mod.name}
                        </Typography>
                      </Box>
                      <LockIcon fontSize="small" sx={{ color: 'warning.main', flexShrink: 0 }} />
                    </Box>
                    {mod.description && (
                      <Typography variant="caption" color="text.disabled" sx={{ flexGrow: 1 }}>
                        {mod.description}
                      </Typography>
                    )}
                    {info && (
                      <Box sx={{ mt: 'auto' }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
                          Included from <strong>{info.planName}</strong> · {info.priceHint}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          fullWidth
                          startIcon={<ArrowUpwardIcon fontSize="small" />}
                          onClick={() => navigate('/settings#billing-plans')}
                        >
                          Upgrade to unlock
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      <ModuleStateSection
        title="Available add-ons"
        caption="Extras you can add to your current plan — no upgrade needed."
        icon={<CreditCardIcon fontSize="small" color="primary" />}
        chip={<Chip label={`${categorized.available.length} to buy`} size="small" variant="outlined" />}
        modules={categorized.available}
        locked
        renderControl={(mod) => {
          const addon = addonByKey.get(mod.key);
          const interval: 'monthly' | 'annual' =
            addon?.monthly_price != null ? 'monthly' : 'annual';
          return (
            <Button
              size="small"
              variant="contained"
              startIcon={<CreditCardIcon fontSize="small" />}
              disabled={buyingAddon === mod.key || !addon}
              onClick={() => handleBuyAddon(mod.key, interval)}
            >
              {buyingAddon === mod.key ? '…' : 'Buy'}
            </Button>
          );
        }}
        getEffectiveEnabled={getEffectiveEnabled}
        iconMap={ICON_MAP}
      />

      {/* Coming 2027 — future modules not yet available */}
      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ScheduleIcon fontSize="small" sx={{ color: 'text.disabled' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: TYPE.semibold, color: 'text.secondary' }}>
            Coming in 2027
          </Typography>
          <Chip label="Future" size="small" variant="outlined" sx={{ color: 'text.disabled', borderColor: 'divider' }} />
        </Box>
        <Grid container spacing={2}>
          {COMING_SOON_MODULES.map((mod) => (
            <Grid item xs={12} sm={6} md={4} key={mod.key}>
              <Card
                sx={{
                  height: '100%',
                  opacity: 0.5,
                  borderLeft: '3px solid transparent',
                  cursor: 'default',
                  userSelect: 'none',
                }}
              >
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, pb: '12px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ color: 'text.disabled' }}>{mod.icon}</Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: TYPE.semibold, color: 'text.secondary' }}>
                        {mod.name}
                      </Typography>
                    </Box>
                    <Tooltip title="Not yet available on this plan">
                      <Chip label="2027" size="small" variant="outlined" sx={{ color: 'text.disabled', borderColor: 'divider', fontSize: '0.65rem' }} />
                    </Tooltip>
                  </Box>
                  <Typography variant="caption" color="text.disabled">
                    {mod.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* A4: diff-driven confirm dialog. */}
      <Dialog
        open={confirmDialog}
        onClose={() => setConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <BrandedDialogTitle>Apply industry template</BrandedDialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Applying <strong>{selectedTemplateName}</strong> to{' '}
            <strong>{activeCompany?.company_name}</strong> will change these modules. Core modules
            are unaffected.
          </DialogContentText>

          {previewFetching && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!previewFetching && preview && (
            <Stack spacing={1.5} divider={<Divider flexItem />}>
              <DiffSection
                title="Will enable"
                color="success.main"
                icon={<EnableIcon fontSize="small" color="success" />}
                keys={preview.will_enable}
                names={names}
                emptyText="Nothing new to enable."
              />
              <DiffSection
                title="Will disable"
                color="error.main"
                icon={<DisableIcon fontSize="small" color="error" />}
                keys={preview.will_disable}
                names={names}
                emptyText="Nothing to disable."
              />
              {preview.plan_blocked.length > 0 && (
                <DiffSection
                  title="Blocked by current plan — won't be enabled"
                  color="warning.main"
                  icon={<LockIcon fontSize="small" color="warning" />}
                  keys={preview.plan_blocked}
                  names={names}
                  emptyText=""
                />
              )}
              {preview.will_enable.length === 0 &&
                preview.will_disable.length === 0 &&
                preview.plan_blocked.length === 0 && (
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Your modules already match this template — applying it will be a no-op.
                  </Alert>
                )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => applyTemplateMutation.mutate(selectedTemplate)}
            disabled={applyTemplateMutation.isPending || previewFetching}
          >
            {applyTemplateMutation.isPending ? 'Applying…' : 'Apply'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// One titled section of module cards for a single billing state. Hides itself
// when the group is empty so the page only shows states that actually apply.
function ModuleStateSection({
  title,
  caption,
  icon,
  chip,
  modules,
  renderControl,
  getEffectiveEnabled,
  iconMap,
  locked,
}: {
  title: string;
  caption: string;
  icon: React.ReactNode;
  chip?: React.ReactNode;
  modules: ModuleConfig[];
  renderControl: (mod: ModuleConfig) => React.ReactNode;
  getEffectiveEnabled: (mod: ModuleConfig) => boolean;
  iconMap: Record<string, React.ReactNode>;
  locked?: boolean;
}) {
  if (!modules.length) return null;
  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        {icon}
        <Typography variant="subtitle1" sx={{ fontWeight: TYPE.semibold }}>
          {title}
        </Typography>
        {chip}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {caption}
      </Typography>
      <Grid container spacing={2}>
        {modules.map((mod) => {
          const enabled = !locked && getEffectiveEnabled(mod);
          return (
            <Grid item xs={12} sm={6} md={4} key={mod.key}>
              <Card
                sx={{
                  height: '100%',
                  opacity: locked ? 0.75 : enabled ? 1 : 0.6,
                  borderLeft: enabled ? '3px solid' : '3px solid transparent',
                  borderColor: enabled ? 'primary.main' : 'transparent',
                  transition: 'all 0.2s',
                }}
              >
                <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, pb: '12px !important' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ color: locked ? 'text.disabled' : 'primary.main' }}>
                        {iconMap[mod.icon || ''] || (
                          <DynamicIcon name={mod.icon} fallback={SettingsIcon} fontSize="medium" />
                        )}
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: TYPE.semibold }}>
                        {mod.name}
                      </Typography>
                    </Box>
                    {renderControl(mod)}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {mod.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

function DiffSection({
  title,
  color,
  icon,
  keys,
  names,
  emptyText,
}: {
  title: string;
  color: string;
  icon: React.ReactNode;
  keys: string[];
  names: Record<string, string>;
  emptyText: string;
}) {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        {icon}
        <Typography variant="subtitle2" sx={{ color, fontWeight: TYPE.semibold }}>
          {title}
        </Typography>
      </Box>
      {keys.length === 0 ? (
        emptyText ? (
          <Typography variant="caption" color="text.secondary">
            {emptyText}
          </Typography>
        ) : null
      ) : (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', pl: 3 }}>
          {keys.map((k) => (
            <Chip key={k} label={names[k] || k} size="small" />
          ))}
        </Box>
      )}
    </Box>
  );
}
